import {
  View,
  TouchableOpacity,
  KeyboardAvoidingView,
  ScrollView,
  Pressable,
  Keyboard,
} from "react-native"
import {
  BottomSheetModal,
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetView,
} from "@gorhom/bottom-sheet"
import {
  type Dispatch,
  type SetStateAction,
  useState,
  useRef,
  useCallback,
} from "react"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import BackButton from "@/assets/icons/back-arrow-dark.svg"
import CheckBoxChecked from "@/assets/icons/checkbox-checked.svg"
import { router } from "expo-router"
import { PrimaryButton } from "@/components/buttons/CommonButtons"
import { BFColors } from "@/constants/Colors"
import { useDisconnectMyLeadsMutation } from "@/queries/user"
import { showErrorToast, showSuccessToast } from "@/components/toasts/AppToast"
import * as AccountEvents from "@/analytics/events/account"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import BFText, { Body, H3 } from "@/components/standard/BFText"
import { BFTextInput } from "@/components/standard/BFTextInput"

interface DisconnectConfirmableButtonProps {
  onPress?: () => void
  disabled?: boolean
}

const DisconnectConfirmableButton = ({
  onPress = () => {},
  disabled = false,
}: DisconnectConfirmableButtonProps) => {
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const onShow = useCallback(() => {
    // TODO segment
    Keyboard.dismiss()
    bottomSheetModalRef.current?.present()
  }, [])

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
      />
    ),
    []
  )

  return (
    <>
      <View className="flex flex-col flex-1 max-h-14">
        <PrimaryButton
          testID={"do-disconnect-button"}
          title="Disconnect My Leads"
          onPress={onShow}
          disabled={disabled}
        />
      </View>
      <BottomSheetModal
        ref={bottomSheetModalRef}
        backdropComponent={renderBackdrop}
        index={0}
        backgroundStyle={{ backgroundColor: BFColors.midnightBlue }}
        handleIndicatorStyle={{ backgroundColor: BFColors.calmBlue }}
        enableDynamicSizing={true}
      >
        <BottomSheetView className="flex flex-col items-center justify-start h-full gap-8 py-5">
          <H3 className="m-auto text-center text-white max-w-[245px] shrink-0 ">
            Are you sure you want to disconnect My Leads?
          </H3>
          <Body className="m-auto text-center text-teal  max-w-[350px] shrink-0 pb-5">
            By selecting Disconnect My Leads you are agreeing to disconnect all
            syncing of your Gmail account to Backflip.{`\n\n`}In doing so, all
            contact information will be deleted from our server and you will no
            longer continue to receive updates for new property lead
            information.
          </Body>
          <View className="w-3/4 h-14">
            <PrimaryButton
              testID={"OkButton"}
              title="I agree"
              onPress={() => {
                onPress()
                bottomSheetModalRef.current?.dismiss()
              }}
            />
          </View>
        </BottomSheetView>
      </BottomSheetModal>
    </>
  )
}

const disconnectReasons = [
  {
    label: "The calculations are incorrect",
    value: "calculations-incorrect",
  },
  {
    label: "The property leads aren't providing enough data",
    value: "incomplete-data",
  },
  {
    label: "I prefer to view property leads on my Gmail account",
    value: "prefer-gmail",
  },
  {
    label: "Other",
    value: "other",
  },
]

interface RadioOptionProps {
  label: string
  value?: string
  selected?: boolean | undefined
}

const RadioOption = ({ label, selected }: RadioOptionProps) => {
  return (
    <View className="flex flex-row items-center justify-start w-full gap-5">
      {selected ? (
        <CheckBoxChecked className="w-[20px] h-[21px]" />
      ) : (
        <View className="border-2 border-dark-grey w-[20px] h-[21px] rounded-full" />
      )}
      <Body className="shrink">{label}</Body>
    </View>
  )
}

interface RadioOptionGroupProps {
  options: typeof disconnectReasons
  defaultOptionValue: string | undefined
  onOptionChange?: (value: string) => void
}

const RadioOptionGroup = ({
  options,
  defaultOptionValue,
  onOptionChange = () => {},
}: RadioOptionGroupProps) => {
  const [selectedOptionValue, setSelectedOptionValue] = useState<
    string | undefined
  >(defaultOptionValue)

  return (
    <View className="flex flex-col justify-start w-full gap-8">
      {options.map((option) => {
        return (
          <Pressable
            testID={`select-${option.value}`}
            key={option.value}
            onPress={() => {
              setSelectedOptionValue(option.value)
              onOptionChange(option.value)
            }}
          >
            <RadioOption
              label={option.label}
              value={option.value}
              selected={option.value === selectedOptionValue}
            />
          </Pressable>
        )
      })}
    </View>
  )
}

interface DisconnectMyLeadsFormProps {
  selectedReason: string | undefined
  setSelectedReason: Dispatch<SetStateAction<string | undefined>>
  reasonExplained: string | undefined
  setReasonExplained: Dispatch<SetStateAction<string | undefined>>
  disabled?: boolean
}

const DisconnectMyLeadsForm: React.FC<DisconnectMyLeadsFormProps> = ({
  selectedReason,
  setSelectedReason,
  reasonExplained,
  setReasonExplained,
  disabled = false,
}: DisconnectMyLeadsFormProps) => {
  return (
    <ScrollView
      className="flex flex-col flex-1 pb-20"
      contentContainerClassName="flex-grow flex flex-col justify-start"
    >
      <View className="flex flex-col flex-1 mt-10">
        <View className="flex flex-col items-center justify-start gap-8 shrink-0 ">
          <RadioOptionGroup
            options={disconnectReasons}
            defaultOptionValue={selectedReason}
            onOptionChange={(value) => {
              setSelectedReason(value)
            }}
          />
        </View>
        <View className="flex-1 p-4 mt-8 bg-white border rounded min-h-56 border-warm-grey">
          <BFTextInput
            multiline
            placeholder="Additional explanation"
            value={reasonExplained}
            onChangeText={(value) => {
              setReasonExplained(value)
            }}
            editable={!disabled}
            enterKeyHint="done"
            className="size-full bg-white  placeholder:text-warm-grey "
          />
        </View>
      </View>
    </ScrollView>
  )
}

const onBack = () => {
  router.back()
}

export default function DisconnectMyLeadsScreen() {
  const insets = useSafeAreaInsets()
  const { isWeb } = useDeviceAttributes()
  const disconnectMutation = useDisconnectMyLeadsMutation()

  useViewedCallback(() => {
    setStatusBarStyle("dark")
    AccountEvents.accountDisconnectMyLeadsViewed()
  })

  const [selectedReason, setSelectedReason] = useState<string | undefined>(
    undefined
  )

  const [reasonExplained, setReasonExplained] = useState<string | undefined>(
    undefined
  )

  const isDisabled =
    disconnectMutation.isPending || typeof selectedReason === "undefined"

  const onSave = () => {
    if (!selectedReason) {
      showErrorToast({ message: "A disconnect reason is required." })
      return
    }

    AccountEvents.myLeadsDisconnectedClicked({
      reason: selectedReason,
      additionalExplanation: reasonExplained,
    })

    disconnectMutation.mutate(
      {
        reason: selectedReason,
        explanation: reasonExplained,
      },
      {
        onSuccess: () => {
          showSuccessToast({
            message: "My Leads successfully disconnected.",
            autoHide: true,
          })
          router.back()
        },
        onError: () => {
          showErrorToast({
            message: "Unable to disconnect. Try again later.",
          })
        },
      }
    )
  }

  return (
    <KeyboardAvoidingView
      className="flex flex-col justify-start flex-1 bg-off-white"
      behavior="padding"
      style={{
        paddingTop: isWeb ? insets.top + 10 : insets.top,
      }}
    >
      <View className="flex flex-col px-8 shrink-0">
        <TouchableOpacity testID={"back-button"} onPress={onBack}>
          <BackButton />
        </TouchableOpacity>
        <BFText
          className={`text-[24px] font-figtree-bold leading-[24px] text-midnight-blue pt-4`}
        >
          Disconnect My Leads
        </BFText>
      </View>
      <View className="flex flex-col justify-between flex-1 h-full px-8 pb-8">
        <DisconnectMyLeadsForm
          selectedReason={selectedReason}
          setSelectedReason={setSelectedReason}
          reasonExplained={reasonExplained}
          setReasonExplained={setReasonExplained}
          disabled={isDisabled}
        />
        <DisconnectConfirmableButton onPress={onSave} disabled={isDisabled} />
      </View>
    </KeyboardAvoidingView>
  )
}
